import { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import Landing from './pages/public pages/Landing';
import Login from './pages/public pages/Login';
import Register from './pages/public pages/Register';
import ForgotPassword from './pages/public pages/ForgotPassword';
import ResetPassword from './pages/public pages/ResetPassword';
import VerifyEmail from './pages/public pages/VerifyEmail';
import Terms from './pages/public pages/Terms';
import Privacy from './pages/public pages/Privacy';
import CommunityGuidelines from './pages/public pages/CommunityGuidelines';
import TermsPrivate from './pages/private pages/TermsPrivate';
import PrivacyPrivate from './pages/private pages/PrivacyPrivate';
import CommunityGuidelinesPrivate from './pages/private pages/CommunityGuidelinesPrivate';
import ForYou from './pages/private pages/feed/ForYou';
import Following from './pages/private pages/feed/Following';
import Profile from './pages/private pages/Profile';
import PostDetail from './pages/private pages/PostDetail';
import Notifications from './pages/private pages/Notifications';
import Create from './pages/private pages/Create';
import PublicRoute from './routes/PublicRoute';
import PrivateRoute from './routes/PrivateRoute';
import Settings from './pages/private pages/Settings';
import ResetPasswordPrivate from './pages/private pages/ResetPassword';
import './App.css';

function AppRoutes() {
  const [isLoggedIn, setIsLoggedIn] = useState(
    () => !!localStorage.getItem("token")
  );

  const location = useLocation();


  // Listen for login events from password reset or other sources
  useEffect(() => {
    const handleUserLogin = () => {
      setIsLoggedIn(true);
    };

    const handleUserLogout = () => {
      setIsLoggedIn(false);
    };

    window.addEventListener('userLogin', handleUserLogin);
    window.addEventListener('userLogout', handleUserLogout);

    return () => {
      window.removeEventListener('userLogin', handleUserLogin);
      window.removeEventListener('userLogout', handleUserLogout);
    };
  }, []);

  const handleLogout = () => {
    // Clear authentication data
    localStorage.removeItem("token");
    localStorage.removeItem("user");

    // Clear all feed-related data
    localStorage.removeItem("feedType");
    sessionStorage.removeItem("feedScrollPosition_foryou");
    sessionStorage.removeItem("feedScrollPosition_following");
    sessionStorage.removeItem("previousLocation");

    // Clear feed caches
    sessionStorage.removeItem("foryouFeedCache");
    sessionStorage.removeItem("followingFeedCache");
    sessionStorage.removeItem("foryouScrollPosition");
    sessionStorage.removeItem("followingScrollPosition");
    sessionStorage.removeItem("navigatedFromFeed");
    sessionStorage.removeItem("navigatedFromFollowing");

    // Clear navigation history
    sessionStorage.removeItem("historyStack");

    // Clear all scroll positions
    const keys = Object.keys(sessionStorage);
    keys.forEach(key => {
      if (key.startsWith('scroll-') || key.startsWith('scrollPos-')) {
        sessionStorage.removeItem(key);
      }
    });

    // Dispatch logout event to notify components
    window.dispatchEvent(new CustomEvent('userLogout'));

    setIsLoggedIn(false);
    // The smart root route will automatically show Landing page when isLoggedIn becomes false
    // No need for window.location.href which causes a flash
  };

  const privateRoutes = [
    "/feed",
    "/following",
    "/profile",
    "/post",
    "/notifications",
    "/create",
    "/legal",
  ];

  const isPrivateRoute = privateRoutes.some((route) =>
    location.pathname.startsWith(route.split("/:")[0])
  );

  const mainContentClass = isLoggedIn ? (isPrivateRoute ? 'private-page' : 'public-page') : '';

  const appClass = isLoggedIn
    ? `with-sidebar ${isPrivateRoute ? 'private-app' : 'public-app'}`
    : 'without-sidebar';


  // Dynamically set theme-color for public vs private pages
  useEffect(() => {
    const publicColor = 'rgb(10, 10, 10)';
    const privateColor = '#111111';
    const targetColor = isLoggedIn && isPrivateRoute ? privateColor : publicColor;

    let meta = document.querySelector('meta[name="theme-color"]') as HTMLMetaElement | null;
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'theme-color');
      document.head.appendChild(meta);
    }
    if (meta.getAttribute('content') !== targetColor) {
      meta.setAttribute('content', targetColor);
    }
  }, [isLoggedIn, isPrivateRoute, location.pathname]);


  // Route element that works for both logged-in and logged-out users
  const ResetPasswordGate: React.FC<{ isLoggedIn: boolean }> = ({ isLoggedIn }) => {
    // Preserve query params (e.g., token)
    if (isLoggedIn) {
      // Send logged-in users to private reset page
      return <ResetPasswordPrivate />;
    }
    // Logged-out users get the public page
    return <ResetPassword />;
  };

  return (
    <div className={`app ${appClass}`}>
      {/* Sidebar Header - only show when logged in */}
      <Navbar isLoggedIn={isLoggedIn} onLogout={handleLogout} />

      {/* Main Content */}
      <div className={`main-content ${mainContentClass}`}>
        <Routes>
          {/* Public Routes */}
          <Route element={<PublicRoute isLoggedIn={isLoggedIn} />}>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login setIsLoggedIn={setIsLoggedIn} />} />
            <Route path="/register" element={<Register setIsLoggedIn={setIsLoggedIn} />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/community-guidelines" element={<CommunityGuidelines />} />
          </Route>

          {/* Reset password - accessible for both states. Chooses the correct layout automatically */}
          <Route path="/reset-password" element={<ResetPasswordGate isLoggedIn={isLoggedIn} />} />

          {/* Private Routes */}
          <Route element={<PrivateRoute isLoggedIn={isLoggedIn} />}>
            <Route path="/feed" element={<ForYou />} />
            <Route path="/following" element={<Following />} />
            <Route path="/profile/:username" element={<Profile />} />
            <Route path="/post/:postId" element={<PostDetail />} />
            <Route path="/notifications" element={<Notifications />} />
            <Route path="/create" element={<Create />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/account/reset-password" element={<ResetPasswordPrivate />} />
            {/* Private mirrors of public legal pages (use private versions) */}
            <Route path="/legal/terms" element={<TermsPrivate />} />
            <Route path="/legal/privacy" element={<PrivacyPrivate />} />
            <Route path="/legal/community-guidelines" element={<CommunityGuidelinesPrivate />} />
          </Route>

          {/* Catch-all route for 404s */}
          <Route path="*" element={<h1>Page not found</h1>} />
        </Routes>
      </div>
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppRoutes />
    </Router>
  );
}

export default App;
